import { ItemValidator } from '../../services/ItemValidator';
import { MandatoryItemsStore } from '../features/tracking/models/MandatoryItemsStore';
import { storageMiddleware } from './middleware/storageMiddleware';
import { provideStudentPanoramaMiddleware } from './middleware/studentPanoramaMiddleware';
import {
  IPersistenceTrackingClient,
  PersistenceTrackingData,
  TrackingData,
} from './middleware/tracking/clients/trackingClient';
import { convertLegacyTrackingDataToCurrentFormat } from './middleware/tracking/decorators/convertLegacyTrackingDataToCurrentFormat';
import { decompressTrackingDataForScorm } from './middleware/tracking/decorators/decompressTrackingDataForScorm';
import { persistedTrackingDataToTrackingData } from './middleware/tracking/decorators/persistedTrackingDataToTrackingData';
import { getChildrenForScormCreator } from './middleware/tracking/selectors/getChildrenForScormCreator';
import { getLegacyPresenterDataCreator } from './middleware/tracking/selectors/getLegacyPresenterDataCreator';
import { prepareTrackingPersistenceMiddleware } from './middleware/tracking/trackingPersistenceMiddleware';
import { convertTrackingIdToItemIdCreator } from './middleware/tracking/utils/itemIdToTrackingIdConverter';
import { Guid } from './model/guid';
import { StudentStore } from './models/Store';
import { blindMapReducer } from './reducers/blindMapReducer';
import { currentHotspotReducer } from './reducers/currentHotspotReducer';
import { currentQuizReducer } from './reducers/currentQuizReducer';
import { currentResourceReducer } from './reducers/currentResourceReducer';
import { currentSequencedFlowPositionReducer } from './reducers/currentSequencedFlowPositionReducer';
import { createItemsReducer } from './reducers/items/itemsReducer';
import { menuStartingMessageReducer } from './reducers/menuStartingMessageReducer';
import { createRootItemsReducer } from './reducers/rootItemsReducer';
import { audioStateReducer } from './reducers/student/audio/audioReducer';
import { currentItemReducer as currentItemStudentReducer } from './reducers/student/currentItemReducer';
import { introductionsDismissedReducer } from './reducers/student/introductionsDismissedReducer';
import { loadDefaultValue as captionsEnabledDefaultStateLoader } from './reducers/student/preferences/captionsEnabledReducer';
import { preferencesReducer } from './reducers/student/preferences/preferencesReducer';
import { selectedMenuItemsReducer as selectedMenuItemsStudentReducer } from './reducers/student/selectedMenuItemsReducer';
import { ItemsTrackingState } from './reducers/student/tracking/itemsTrackingReducer';
import { QuestionWidgetTrackingState } from './reducers/student/tracking/questionWidgetTrackingReducer';
import { TrackedQuizState } from './reducers/student/tracking/trackedQuizReducer';
import { createTrackingState } from './reducers/student/tracking/trackingState';
import { createStudentSideMenuReducer } from './reducers/studentSideMenuReducer';
import { WidgetDataRepository } from './repository/widgets/widgetDataRepository';
import {ConvertItemsFunc, convertItemsFunctionCreator, createNoParentId, Item} from '@ael/core-api';
import {
  applyMiddleware,
  combineReducers as reduxCombineReducers,
  compose,
  configureStore,
  Middleware,
  Store,
} from '@reduxjs/toolkit';
import * as Immutable from 'immutable';
import { List, OrderedMap } from 'immutable';
import thunkMiddleware from 'redux-thunk';
import {ProjectViewModel} from "../../config/configs";

/*
In student mode we want to exclude items and content items with validation errors.
Returns false if we're in manager mode or item is valid, true if we have invalid item or content item in student mode.
 */
const isInvalidItemInStudentMode = (item: Item) => {
  return !ItemValidator.isValid(item);
};

function convertServerContentToDefaultState(projectViewModel: ProjectViewModel) {
  WidgetDataRepository.populateCache(projectViewModel.initializationWidgetData ?? []);

  const convertItemsFunction: ConvertItemsFunc = convertItemsFunctionCreator(isInvalidItemInStudentMode);
  const convertedItems = convertItemsFunction(projectViewModel.items ?? [], createNoParentId());

  return {
    contentItems: convertedItems.itemsMap ?? OrderedMap<string, Item>(),
    rootLevelItems: convertedItems.rootLevelItemIds ?? List<string>(),
  };
}

function createStartingItemsTrackingState(allItemsMap: Immutable.OrderedMap<Guid, Item>): ItemsTrackingState {
  const mandatoryItems = new MandatoryItemsStore();
  mandatoryItems.populateFromItems(allItemsMap.toList());

  return {
    finishedItems: Immutable.Set(),
    trackedItems: mandatoryItems.toJS(),
  };
}

// Student store as a function as we want to reset it every time mode of app is changed
let studentStore: Store<StudentStore> | null = null;
//any automagic here???
// export const getStudentStoreDispatch = () =>
//   getStudentStore().dispatch as (action: AnyAction | StudentStoreThunkAction) => void;

function getTrackedDataFromPersistedDataAndStartState(
  startState: { rootLevelItems: Immutable.List<Guid>; contentItems: Immutable.OrderedMap<string, Item> },
  persistedTrackingData: PersistenceTrackingData,
  persistenceTrackingClient: IPersistenceTrackingClient
): TrackingData {
  const convertTrackingIdToItemId = convertTrackingIdToItemIdCreator(() => startState.contentItems);
  let trackedData = persistedTrackingDataToTrackingData(persistedTrackingData, convertTrackingIdToItemId);
  if (persistenceTrackingClient.name === 'SCORM') {
    const getChildrenForScorm = getChildrenForScormCreator(() => startState.contentItems);
    trackedData = decompressTrackingDataForScorm(trackedData, getChildrenForScorm);
  }

  if (persistenceTrackingClient.doesSupportLegacyData) {
    const getLegacyPresenterData = getLegacyPresenterDataCreator(() => startState.contentItems);
    trackedData = convertLegacyTrackingDataToCurrentFormat(trackedData, getLegacyPresenterData);
  }

  return trackedData;
}

export const getStudentStore = (
    projectViewModel: ProjectViewModel,
  persistenceTrackingClient: IPersistenceTrackingClient,
  persistedTrackingData: PersistenceTrackingData,
  isInPreview: boolean,
  regenerate?: boolean
): Store<StudentStore> => {
  if (studentStore && !regenerate) {
    return studentStore;
  }

  const startState = convertServerContentToDefaultState(projectViewModel);
  const trackingStartState = createStartingItemsTrackingState(startState.contentItems);

  const trackingData = getTrackedDataFromPersistedDataAndStartState(
    startState,
    persistedTrackingData,
    persistenceTrackingClient
  );

  // Picked this weird logic out of init action reducer
  const updateItemsTrackingState: ItemsTrackingState = {
    finishedItems: trackingStartState.finishedItems.merge(
      trackingData.finishedItems ? trackingData.finishedItems : Immutable.Set()
    ),
    trackedItems: trackingStartState.trackedItems,
  };
  const widgetStartState: QuestionWidgetTrackingState = {
    previouslySelectedOptionsForItems: trackingData.widgetTrackingData,
  };

  const trackedQuizStartState: TrackedQuizState = {
    quizId: projectViewModel.additionalSettings.quizGuidForTrackingPassingScore,
    score: persistedTrackingData.quizStatus?.score || null,
    passed: persistedTrackingData.quizStatus?.passed || null,
  };

  studentStore = configureStore({
    reducer: reduxCombineReducers<StudentStore>({
      items: createItemsReducer(startState.contentItems),
      rootItems: createRootItemsReducer(startState.rootLevelItems),
      currentItem: currentItemStudentReducer,
      currentSequencedFlowPosition: currentSequencedFlowPositionReducer,
      selectedMenuItems: selectedMenuItemsStudentReducer,
      sideMenu: createStudentSideMenuReducer(isInPreview, projectViewModel.config.isLinearModeEnabled),
      currentHotspot: currentHotspotReducer,
      introductionsDismissed: introductionsDismissedReducer,
      tracking: createTrackingState(updateItemsTrackingState, widgetStartState, false, trackedQuizStartState),
      currentQuiz: currentQuizReducer,
      blindMap: blindMapReducer,
      preferences: preferencesReducer,
      menuStartingMessage: menuStartingMessageReducer,
      currentResource: currentResourceReducer,
      audioState: audioStateReducer,
    }),
    enhancers: [
      compose(
        applyMiddleware(
          thunkMiddleware,
          provideStudentPanoramaMiddleware(),
          prepareTrackingPersistenceMiddleware(persistenceTrackingClient) as Middleware,
            storageMiddleware
        )
      ),
    ],
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
      }),
    preloadedState: {
      preferences: {
        captionsEnabled: captionsEnabledDefaultStateLoader(),
      },
    } as any, // Seems like common issue https://github.com/reduxjs/redux/issues/3670
  });

  return studentStore;
};
